import { TIE_BONUS, type WarState } from '../../engine/games/casinoWar';
import type { Card } from '../../engine/cards';
import { money, withCommas } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** The dealer's card turns over this long after yours shows. */
const FLIP_MS = 600;
/** War: three burn cards, one every BURN_MS, before the new pair. */
const BURN_MS = 300;
const LOST_MS = 900;
const BANNER_MS = 1200;
const END_HOLD_MS = 300;

function Side({ name, cards, hideLast }: { name: string; cards: Card[]; hideLast: boolean }) {
  return (
    <div className="war-side">
      <div className="war-name">{name}</div>
      <div className="cards war-cards">
        {cards.map((c, i) => (i === cards.length - 1 && hideLast ? <div key={i} className="big-card"><PlayingCard hidden /></div> : <div key={i} className={i < cards.length - 1 ? 'war-earlier' : 'big-card'}><PlayingCard card={c} /></div>))}
      </div>
    </div>
  );
}

export function CasinoWarTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.war?.state as WarState | undefined;
  const anim = snapshot.anim;
  const frames = anim?.frames ?? [];
  const war = frames.some((f) => f.includes('Burn'));
  const luckFrame = secondChanceFrame(snapshot);
  // The dealer card that would have beaten you (last card on the frame's Dealer line).
  const beat = luckFrame ? cardsIn(luckFrame.split('\n').find((l) => l.startsWith('Dealer:')) ?? '').at(-1) ?? null : null;
  const burnMs = war ? 3 * BURN_MS : 0;
  const flipAt = burnMs + FLIP_MS;
  const luckAt = beat !== null ? flipAt + LOST_MS : null;
  const newFlipAt = luckAt !== null ? luckAt + BANNER_MS + FLIP_MS : flipAt;
  const elapsed = useAnimClock(snapshot, actions, newFlipAt + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, luckAt, () => ({ what: 'A dealer card that beat you is drawn again.', saved: true }), setBurst);

  if (!state || state.phase === 'idle') {
    return (
      <>
        <Stage className="war-stage"><p className="felt-hint">One card each. Higher wins; aces high. A tie means war.</p></Stage>
        <Controls snapshot={snapshot} actions={actions} />
      </>
    );
  }

  // What's on the table right now during the animation.
  let player = state.player;
  let dealer = state.dealer;
  let dealerHidden = false;
  let burns = 0;
  if (anim) {
    if (war) {
      burns = Math.min(3, Math.floor(elapsed / BURN_MS) + 1);
      if (elapsed < burnMs) {
        player = state.player.slice(0, -1);
        dealer = state.dealer.slice(0, -1);
      }
    }
    if (elapsed >= burnMs) {
      if (beat !== null && elapsed >= flipAt && elapsed < luckAt! + BANNER_MS) dealer = [...state.dealer.slice(0, -1), beat];
      else if (elapsed < flipAt || (beat !== null && elapsed < newFlipAt)) dealerHidden = true;
    }
  }
  const tie = state.phase === 'tie' && !anim;
  const net = state.returned - state.wagered;

  return (
    <>
      <Stage className="war-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <Side name="Dealer" cards={dealer} hideLast={dealerHidden} />
        {/* The burn cards lie over the gap between the two sides, so your
            card never moves while they're dealt (user, 2026-09-27). */}
        <div className="war-mid">
          <div className="war-vs">vs</div>
          {war && anim && elapsed < burnMs + 200 && (
            <div className="burns" aria-label="Burn cards">
              {Array.from({ length: burns }, (_, i) => <PlayingCard key={i} hidden />)}
              <span>Burn {burns}</span>
            </div>
          )}
        </div>
        <Side name="You" cards={player} hideLast={false} />
        {tie && <p className="tc-hint">A tie! Go to war by matching your bet: win and you take the raise; tie again and you get {money(TIE_BONUS * state.bet)} on top. Or surrender half your bet.</p>}
        <StatusLine snapshot={snapshot} result={state.phase === 'done' && !anim ? withCommas(state.result) : undefined} tone={state.phase === 'done' ? (net > 0 ? 'win' : net < 0 ? 'loss' : '') : ''} />
        {state.phase === 'done' && !anim && state.wagered > state.bet && <p className="craps-note">Wagered {money(state.wagered)} with the war.</p>}
      </Stage>
      <Controls snapshot={snapshot} actions={actions} primaryId="war" onAct={() => setBurst(null)} />
    </>
  );
}
